import { DlqRedriveInfoApiFactory } from "../../../lusca-search-api/LuscaSearchApiFactory";
import { Stage } from "../../../lusca-search-api/generated-src";

export interface UploadResult {
  s3Uri: string;
  etag?: string;
}

interface ExecuteDlqRedriveParams {
  stage: Stage;
  messageIds?: string[];
  messageIdsS3Uri?: string;
  messageIdsS3Etag?: string;
  mcmId?: string;
  dryRun: boolean;
  deleteWithoutRedriving: boolean;
}

export const useExecuteDlqRedrive = () => {
  // Upload a CSV to S3 and return its URI + ETag. Split from executeRedrive so
  // delete-without-redrive can upload once and reuse the same object (a
  // re-upload changes the ETag and breaks the MCM match).
  const uploadFile = async (
    file: File,
    stage: Stage
  ): Promise<UploadResult> => {
    const dlqRedriveApi = DlqRedriveInfoApiFactory();

    const uploadUrlResponse = await dlqRedriveApi.getDLQRedriveUploadUrl(
      stage,
      file.name
    );

    if (!uploadUrlResponse.data?.uploadUrl || !uploadUrlResponse.data?.s3Uri) {
      throw new Error("Failed to get upload URL");
    }

    const uploadResponse = await fetch(uploadUrlResponse.data.uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": "text/csv",
      },
    });

    if (!uploadResponse.ok) {
      throw new Error("Failed to upload file to S3");
    }

    const etagHeader = uploadResponse.headers.get("ETag");
    const etag = etagHeader?.replace(/"/g, "");

    return {
      s3Uri: uploadUrlResponse.data.s3Uri,
      etag,
    };
  };

  // Start (or dry-run) a redrive with pre-resolved S3 URI/ETag or inline
  // messageIds. Never uploads, so the ETag stays stable across the MCM gap.
  const executeRedrive = async ({
    stage,
    messageIds,
    messageIdsS3Uri,
    messageIdsS3Etag,
    mcmId,
    dryRun,
    deleteWithoutRedriving,
  }: ExecuteDlqRedriveParams) => {
    const dlqRedriveApi = DlqRedriveInfoApiFactory();

    const response = await dlqRedriveApi.postDLQRedriveInfo(stage, {
      messageIds,
      messageIdsS3Uri,
      messageIdsS3Etag,
      mcmId,
      dryRun,
      deleteWithoutRedriving,
    });

    return response.data;
  };

  return { uploadFile, executeRedrive };
};
